/**
 * Venice: a day in the city on the water. A gondola from the pier by the square (forty minutes on the canals, the
 * gondolier rowing standing at the stern), the bell tower's lift to the top for the view, a coffee at the café on the
 * square with the little orchestra playing. Prices in euros, at the going rate.
 */
import { advance, spend, type LifeEvent, type LifeState } from './life.ts';
import { EUR_NGN } from './travel.ts';

const eur = (n: number) => Math.round((n * EUR_NGN) / 100) * 100;
export const GONDOLA_EUR = 90, CAMPANILE_EUR = 10, CAFFE_EUR = 14;
export const gondolaPrice = () => eur(GONDOLA_EUR);
export const campanilePrice = () => eur(CAMPANILE_EUR);
export const caffePrice = () => eur(CAFFE_EUR);

interface VeniceState { gondolas?: number; campanile?: number; caffeDay?: number }
const of = (s: LifeState): VeniceState => ((s as LifeState & { venice?: VeniceState }).venice ??= {});

export function buyGondola(s: LifeState): { ok: boolean; line: string } {
  if (!spend(s, gondolaPrice(), 'A gondola ride, Venice')) return { ok: false, line: `A gondola is €${GONDOLA_EUR}: you can't afford it.` };
  const v = of(s); v.gondolas = (v.gondolas ?? 0) + 1;
  if (v.gondolas === 1) s.history.push({ t: s.minutes, text: 'A first gondola ride, on the canals of Venice.' });
  return { ok: true, line: v.gondolas === 1 ? 'The gondolier takes your hand as you step down into the boat. It rocks, then steadies.' : 'Back in a gondola. You step down like a Venetian now.' };
}
/** off the gondola: a happy forty minutes */
export function gondolaDone(s: LifeState): { line: string; events: LifeEvent[] } {
  s.needs.fun = Math.min(100, s.needs.fun + 22);
  return { line: 'Back at the pier. You tip the gondolier; he lifts his hat.', events: advance(s, 40, { busy: true }) };
}

export function buyCampanile(s: LifeState): { ok: boolean; line: string; events: LifeEvent[] } {
  if (!spend(s, campanilePrice(), 'The bell tower, Venice')) return { ok: false, line: `The lift up is €${CAMPANILE_EUR}: you can't afford it.`, events: [] };
  const v = of(s); v.campanile = (v.campanile ?? 0) + 1;
  s.needs.fun = Math.min(100, s.needs.fun + 10);
  return { ok: true, line: 'The lift hums up inside the tower. At the top: the bells over your head, the whole city under you.', events: advance(s, 25, { busy: true }) };
}

export function buyCaffe(s: LifeState): { ok: boolean; line: string; events: LifeEvent[] } {
  if (!spend(s, caffePrice(), 'A coffee on the square, Venice')) return { ok: false, line: `A coffee here is €${CAFFE_EUR} (it's the music you're paying for): you can't afford it.`, events: [] };
  const v = of(s), first = v.caffeDay !== Math.floor(s.minutes / 1440);
  v.caffeDay = Math.floor(s.minutes / 1440);
  s.needs.hunger = Math.min(100, s.needs.hunger + 5);
  s.needs.fun = Math.min(100, s.needs.fun + (first ? 8 : 2));
  s.needs.energy = Math.min(100, s.needs.energy + 6);
  return { ok: true, line: first ? 'A cappuccino on a silver tray, a white-jacketed waiter, the orchestra across the square playing a waltz. Fourteen euros. Worth every cent.' : 'Another coffee, another waltz.', events: advance(s, 20, { busy: true }) };
}
